import React, { useState } from "react";
import { Link } from "react-router-dom";
import { VideoItem, saveToHistory } from "@/lib/videosApi";
import { Play } from "lucide-react";

interface VideoCardProps {
  video: VideoItem;
  collectionSlug?: string;
  autoplayOnClick?: boolean;
}

export const VideoCard: React.FC<VideoCardProps> = ({
  video,
  collectionSlug,
  autoplayOnClick = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const watchHref = collectionSlug
    ? `/videos/course/${collectionSlug}/${video.id}`
    : `/videos/watch/${video.id}`;

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Save to history
    saveToHistory({
      id: video.id,
      t: video.title,
      href: watchHref,
    });

    if (autoplayOnClick) {
      setIsPlaying(true);
    }
  };

  const thumbnailSrc =
    video.thumbnail || `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;

  return (
    <div className="group flex flex-col space-y-2.5">
      {/* 9:16 Aspect ratio container */}
      <div className="relative aspect-[9/16] w-full rounded-[14px] overflow-hidden bg-slate-100 border border-slate-200/80 shadow-xs group-hover:border-[#E8850F] transition-colors">
        {isPlaying ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&playsinline=1`}
            title={video.title}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            <img
              src={thumbnailSrc}
              alt={`Thumbnail for ${video.title}`}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-300"
            />

            {/* Overlays */}
            {/* Top-left: Short tag if short */}
            {video.short && (
              <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-[10px] font-bold tracking-wider bg-[#E8850F] text-[#1F1306] uppercase shadow-xs">
                Short
              </span>
            )}

            {/* Bottom-right: Duration badge */}
            {video.duration && (
              <span className="absolute bottom-2.5 right-2.5 px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-900/80 text-white backdrop-blur-xs">
                {video.duration}
              </span>
            )}

            {/* Play Button Overlay */}
            <button
              onClick={handlePlayClick}
              aria-label={`Play video: ${video.title}`}
              className="absolute inset-0 flex items-center justify-center bg-slate-900/20 group-hover:bg-slate-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706]"
            >
              <div className="w-12 h-12 rounded-full bg-[#E8850F] text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-5 h-5 fill-white ml-0.5" />
              </div>
            </button>
          </>
        )}
      </div>

      {/* Info below thumbnail */}
      <div className="space-y-1">
        <Link
          to={watchHref}
          className="line-clamp-2 text-xs sm:text-sm font-semibold text-slate-900 hover:text-[#9A5207] leading-snug transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] rounded"
        >
          {video.title}
        </Link>
        <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500 font-medium">
          {video.category && (
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
              {video.category}
            </span>
          )}
          {video.date && (
            <time dateTime={video.date}>
              {new Date(video.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </time>
          )}
        </div>
      </div>
    </div>
  );
};

export const VideoGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 min-[1400px]:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
      {children}
    </div>
  );
};
